/**
 * APC Algolia Insights — Analytics & Event Tracking
 *
 * Tracks user interactions with Algolia-powered search and collection pages:
 * - Click events: when a user clicks a product from search/collection results
 * - Conversion events: when a user adds a product to cart after search/collection
 * - View events: when product cards are visible in the viewport
 *
 * Uses the Algolia Insights REST API directly (no external library).
 * Reads configuration from window.apcAlgolia (set by apc-algolia-config.liquid).
 *
 * @module apc-algolia-insights
 */

import { debounce } from '@theme/utilities';

// ============================================================
// Configuration
// ============================================================

/** @type {{ appId: string, searchApiKey: string, features: { analytics: boolean }, indexes: { products: string } } | null} */
// @ts-ignore — set by apc-algolia-config.liquid
const config = window.apcAlgolia || null;

const INSIGHTS_API_URL = 'https://insights.algolia.io/1/events';
const USER_TOKEN_KEY = 'apc_algolia_user_token';

// ============================================================
// User Token Management
// ============================================================

/**
 * Get or create a persistent anonymous user token.
 * Stored in localStorage so it persists across sessions.
 * @returns {string}
 */
function getUserToken() {
  try {
    let token = localStorage.getItem(USER_TOKEN_KEY);
    if (token) return token;

    // Generate a random token: "anon-" + 16 hex chars
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    token = 'anon-' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

    localStorage.setItem(USER_TOKEN_KEY, token);
    return token;
  } catch {
    // Fallback if localStorage is unavailable
    return 'anon-' + Math.random().toString(36).slice(2, 18);
  }
}

// ============================================================
// Insights API
// ============================================================

/**
 * Send events to the Algolia Insights API.
 * @param {object[]} events - Array of Algolia event objects
 */
function sendEvents(events) {
  if (!config?.appId || !config?.searchApiKey) return;
  if (events.length === 0) return;

  console.debug('[APC Algolia Insights]', events);

  const payload = JSON.stringify({ events });

  // Use sendBeacon for reliability (fires even on page unload)
  // Falls back to fetch if sendBeacon is unavailable or fails
  const sent =
    typeof navigator.sendBeacon === 'function' &&
    navigator.sendBeacon(
      `${INSIGHTS_API_URL}?X-Algolia-Application-Id=${encodeURIComponent(config.appId)}&X-Algolia-API-Key=${encodeURIComponent(config.searchApiKey)}`,
      new Blob([payload], { type: 'text/plain' }),
    );

  if (!sent) {
    fetch(INSIGHTS_API_URL, {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': config.appId,
        'X-Algolia-API-Key': config.searchApiKey,
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: true,
    }).catch(() => {
      // Silently fail — analytics should never break the page
    });
  }
}

// ============================================================
// Event Builders
// ============================================================

/**
 * Build a "clicked object after search" event.
 * @param {string} objectID
 * @param {number} position - 1-based position in results
 * @param {string} queryID - Algolia queryID from the search response
 * @returns {object}
 */
function buildClickEvent(objectID, position, queryID) {
  return {
    eventType: 'click',
    eventName: 'Product Clicked',
    index: config?.indexes.products || '',
    userToken: getUserToken(),
    objectIDs: [objectID],
    positions: [position],
    queryID: queryID.replace('queryID:', ''),
    timestamp: Date.now(),
  };
}

/**
 * Build a "clicked object" event (without search context, e.g. collection browsing).
 * @param {string} objectID
 * @returns {object}
 */
function buildClickEventNoSearch(objectID) {
  return {
    eventType: 'click',
    eventName: 'Product Clicked',
    index: config?.indexes.products || '',
    userToken: getUserToken(),
    objectIDs: [objectID],
    timestamp: Date.now(),
  };
}

/**
 * Build a "converted after search" event (add to cart).
 * @param {string} objectID
 * @param {string} queryID
 * @returns {object}
 */
function buildConversionEvent(objectID, queryID) {
  return {
    eventType: 'conversion',
    eventName: 'Product Added to Cart',
    index: config?.indexes.products || '',
    userToken: getUserToken(),
    objectIDs: [objectID],
    queryID: queryID.replace('queryID:', ''),
    timestamp: Date.now(),
  };
}

/**
 * Build a "converted object" event (add to cart without search context).
 * @param {string} objectID
 * @returns {object}
 */
function buildConversionEventNoSearch(objectID, queryID) {
  return {
    eventType: 'conversion',
    eventName: 'Product Added to Cart',
    index: config?.indexes.products || '',
    userToken: getUserToken(),
    objectIDs: [objectID],
    queryID: queryID.replace('queryID:', ''),
    timestamp: Date.now(),
  };
}

/**
 * Build a "viewed objects" event.
 * @param {string[]} objectIDs - Up to 20 object IDs
 * @param {string} queryID - Algolia queryID from the search response
 * @returns {object}
 */
function buildViewEvent(objectIDs, queryID) {
  return {
    eventType: 'view',
    eventName: 'Products Viewed',
    index: config?.indexes.products || '',
    userToken: getUserToken(),
    objectIDs: objectIDs.slice(0, 20),
    queryID: queryID.replace('queryID:', ''),
    timestamp: Date.now(),
  };
}

// ============================================================
// Click Tracking
// ============================================================

/**
 * Store for mapping productId → Algolia search context.
 * Used to correlate cart add events with the originating search.
 * @type {Map<string, { objectID: string, queryID: string }>}
 */
const searchContextMap = new Map();

/**
 * Handle clicks on Algolia product cards.
 * Delegates from the results-list container.
 */
function initClickTracking() {
  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const card = target.closest('[data-algolia-object-id]');
    if (!(card instanceof HTMLElement)) return;

    const objectID = card.dataset.algoliaObjectId;
    if (!objectID) return;

    const queryID = card.dataset.algoliaQueryId;
    const position = parseInt(card.dataset.algoliaPosition || '0', 10);

    // Store search context for potential cart conversion
    searchContextMap.set(objectID, { objectID, queryID: queryID || '' });

    if (queryID && position > 0) {
      sendEvents([buildClickEvent(objectID, position, queryID)]);
    } else {
      sendEvents([buildClickEventNoSearch(objectID)]);
    }
  });
}

// ============================================================
// Conversion Tracking (Add to Cart)
// ============================================================

/**
 * Listen for cart:update events and track conversions.
 * Correlates the added product with any prior Algolia search context.
 */
function initConversionTracking() {
  document.addEventListener('cart:update', (/** @type {CustomEvent} */ event) => {
    const data = event.detail?.data;
    if (!data?.variantId) return;

    const variantId = String(data.variantId);

    // Check if this product was clicked from an Algolia search
    const context = searchContextMap.get(variantId);
    if (context?.queryID) {
      sendEvents([buildConversionEvent(context.objectID, context.queryID)]);
    } else {
      // Product may have been added from a collection page (no queryID)
      sendEvents([buildConversionEventNoSearch(variantId, context?.queryID || '')]);
    }
  });
}

// ============================================================
// View Tracking (Intersection Observer)
// ============================================================

const handleResultsRendered = debounce(async (event) => {
  const { ObjectIDs, queryID = '' } = event.detail;

  if (!ObjectIDs.length) return;
  sendEvents([buildViewEvent(ObjectIDs, queryID)]);
}, 100);

function initViewTracking() {
  document.addEventListener('algolia:results-rendered', handleResultsRendered);
}

// ============================================================
// Initialization
// ============================================================

function init() {
  if (!config?.features?.analytics) return;
  if (!config?.appId || !config?.searchApiKey) return;

  initClickTracking();
  initConversionTracking();
  initViewTracking();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
